// src/services/patientService.js 
import apiService from './api';

const patientService = {
  
  
  
  /**
   * Get patient profile
   * @returns {Promise} Patient profile data
   */
  getProfile: async () => {
    return await apiService.patients.getProfile();
  },

  /**
   * Update patient profile
   * @param {Object} profileData 
   * @returns {Promise} 
   */
  updateProfile: async (profileData) => {
    return await apiService.patients.updateProfile(profileData);
  },

  /**
   * Upload profile avatar
   * @param {FormData} formData 
   * @returns {Promise} 
   */
  uploadAvatar: async (formData) => {
    return await apiService.users.uploadAvatar(formData);
  },

  /**
   * Get patient by ID (admin/doctor access)
   * @param {string} id - Patient ID
   * @returns {Promise} Patient data
   */
  getPatientById: async (id) => {
    return await apiService.patients.getById(id);
  },

 

  /**
   * Get all patient consultations
   * @param {Object} params 
   * @returns {Promise} 
   */
  getConsultations: async (params = {}) => {
    return await apiService.consultations.getAll(params);
  },

  /**
   * Get consultation by ID
   * @param {string} id 
   * @returns {Promise} 
   */
  getConsultationById: async (id) => {
    return await apiService.consultations.getById(id);
  },

  /**
   * Book a new consultation
   * @param {Object} consultationData 
   * @returns {Promise}
   */
  bookConsultation: async (consultationData) => {
    return await apiService.consultations.create(consultationData);
  },

  /**
   * Cancel consultation
   * @param {string} id 
   * @param {string} reason 
   * @returns {Promise} 
   */
  cancelConsultation: async (id, reason) => {
    return await apiService.consultations.cancel(id, reason);
  },

  /**
   * Rate completed consultation
   * @param {string} id
   * @param {Object} ratingData 
   * @returns {Promise} 
   */
  rateConsultation: async (id, ratingData) => {
    try {
      const response = await apiService.post(`/consultations/${id}/rate`, ratingData);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to rate consultation');
    }
  },

  // ==================== MEDICAL HISTORY ====================

  /**
   * Add allergy
   * @param {Object} allergyData - Allergy information
   * @returns {Promise} Updated allergies list
   */
  addAllergy: async (allergyData) => {
    return await apiService.patients.addAllergy(allergyData);
  },

  /**
   * Remove allergy
   * @param {string} allergyId - Allergy ID
   * @returns {Promise} Updated allergies list
   */
  removeAllergy: async (allergyId) => {
    return await apiService.patients.removeAllergy(allergyId);
  },

  /**
   * Add medical condition
   * @param {Object} conditionData - Medical condition information
   * @returns {Promise} Updated medical history
   */
  addMedicalCondition: async (conditionData) => {
    return await apiService.patients.addMedicalCondition(conditionData);
  },

  /**
   * Get patient medical history
   * @returns {Promise} Medical history
   */
  getMedicalHistory: async () => {
    const profile = await apiService.patients.getProfile();
    return {
      success: true,
      data: {
        allergies: profile.data?.allergies || [],
        medicalHistory: profile.data?.medicalHistory || [],
        chronicConditions: profile.data?.chronicConditions || [],
        surgeries: profile.data?.surgeries || [],
        familyHistory: profile.data?.familyHistory || []
      }
    };
  },

  // ==================== MEDICATIONS ====================

  /**
   * Add medication
   * @param {Object} medicationData - Medication information
   * @returns {Promise} Updated medications list
   */
  addMedication: async (medicationData) => {
    return await apiService.patients.addMedication(medicationData);
  },

  /**
   * Stop medication
   * @param {string} medicationId - Medication ID
   * @returns {Promise} Updated medications list
   */
  stopMedication: async (medicationId) => {
    return await apiService.patients.stopMedication(medicationId);
  },

  /**
   * Get medications due for refill
   * @param {number} daysAhead - Number of days to look ahead
   * @returns {Promise} Medications due for refill
   */
  getMedicationRefills: async (daysAhead = 7) => {
    return await apiService.patients.getMedicationRefills(daysAhead);
  },

  // ==================== VITALS ====================

  /**
   * Update patient vitals
   * @param {Object} vitalsData - Vitals information
   * @returns {Promise} Updated vitals
   */
  updateVitals: async (vitalsData) => {
    return await apiService.patients.updateVitals(vitalsData);
  },

  // ==================== IMMUNIZATIONS ====================

  /**
   * Add immunization
   * @param {Object} immunizationData - Immunization information
   * @returns {Promise} Updated immunizations list
   */
  addImmunization: async (immunizationData) => {
    return await apiService.patients.addImmunization(immunizationData);
  },

  /**
   * Get upcoming immunizations
   * @returns {Promise} Upcoming immunizations
   */
  getUpcomingImmunizations: async () => {
    return await apiService.patients.getUpcomingImmunizations();
  },

  // ==================== INSURANCE ====================

  /**
   * Add insurance
   * @param {Object} insuranceData - Insurance information
   * @returns {Promise} Updated insurance list
   */
  addInsurance: async (insuranceData) => {
    return await apiService.patients.addInsurance(insuranceData);
  },

  /**
   * Update insurance
   * @param {string} insuranceId - Insurance ID
   * @param {Object} insuranceData - Updated insurance information
   * @returns {Promise} Updated insurance
   */
  updateInsurance: async (insuranceId, insuranceData) => {
    return await apiService.patients.updateInsurance(insuranceId, insuranceData);
  },

  /**
   * Remove insurance
   * @param {string} insuranceId - Insurance ID
   * @returns {Promise} Updated insurance list
   */
  removeInsurance: async (insuranceId) => {
    return await apiService.patients.removeInsurance(insuranceId);
  },

  // ==================== DOCUMENTS ====================

  /**
   * Upload medical document
   * @param {Object} documentData - Document information
   * @returns {Promise} Upload response
   */
  uploadMedicalDocument: async (documentData) => {
    return await apiService.patients.uploadDocument(documentData);
  },

  /**
   * Delete medical document
   * @param {string} documentId - Document ID
   * @returns {Promise} Deletion response
   */
  deleteMedicalDocument: async (documentId) => {
    return await apiService.patients.deleteDocument(documentId);
  },

  // ==================== HEALTH SUMMARY ====================

  /**
   * Get health summary
   * @returns {Promise} Health summary
   */
  getHealthSummary: async () => {
    return await apiService.patients.getHealthSummary();
  },

  // ==================== PREFERRED PROVIDERS ====================

  /**
   * Update preferred providers
   * @param {Object} providersData - Preferred providers information
   * @returns {Promise} Updated providers
   */
  updatePreferredProviders: async (providersData) => {
    return await apiService.patients.updatePreferredProviders(providersData);
  },

  // ==================== PRESCRIPTIONS ====================

  /**
   * Get all patient prescriptions
   * @param {Object} params - Query parameters
   * @returns {Promise} List of prescriptions
   */
  getPrescriptions: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await apiService.get(`/prescriptions${queryString ? `?${queryString}` : ''}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch prescriptions');
    }
  },

  /**
   * Get prescription by ID
   * @param {string} id - Prescription ID
   * @returns {Promise} Prescription details
   */
  getPrescriptionById: async (id) => {
    try {
      const response = await apiService.get(`/prescriptions/${id}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch prescription');
    }
  },

 // ==================== MEDICINE ORDERS ====================

/**
 * Get all medicine orders
 * @param {Object} params - Query parameters
 * @returns {Promise} List of orders
 */
getOrders: async (params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const response = await apiService.get(`/orders${queryString ? `?${queryString}` : ''}`);
    return response;
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch orders');
  }
},

/**
 * Get order by ID
 * @param {string} id - Order ID
 * @returns {Promise} Order details
 */
getOrderById: async (id) => {
  try {
    const response = await apiService.get(`/orders/${id}`);
    return response;
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch order');
  }
},

/**
 * Create new medicine order
 * @param {Object} orderData - Order details
 * @returns {Promise} Created order
 */
createOrder: async (orderData) => {
  try {
    const response = await apiService.post('/orders', orderData);
    return response;
  } catch (error) {
    throw new Error(error.message || 'Failed to create order');
  }
},

/**
 * Cancel order
 * @param {string} id - Order ID
 * @param {string} reason - Cancellation reason
 * @returns {Promise} Cancellation response
 */
cancelOrder: async (id, reason) => {
  try {
    const response = await apiService.put(`/orders/${id}/cancel`, { reason });
    return response;
  } catch (error) {
    throw new Error(error.message || 'Failed to cancel order');
  }
},

/**
 * Rate an order
 * @param {string} id - Order ID
 * @param {Object} ratingData - Rating details (rating score and comment)
 * @returns {Promise} Rating response
 */
rateOrder: async (id, ratingData) => {
  try {
    const response = await apiService.post(`/orders/${id}/rate`, ratingData);
    return response;
  } catch (error) {
    throw new Error(error.message || 'Failed to rate order');
  }
},

  // ==================== PHARMACIES ====================

  /**
   * Get all pharmacies
   * @param {Object} params - Query parameters
   * @returns {Promise} List of pharmacies
   */
  getPharmacies: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await apiService.get(`/pharmacies${queryString ? `?${queryString}` : ''}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch pharmacies');
    }
  },

  /**
   * Get pharmacy by ID
   * @param {string} id - Pharmacy ID
   * @returns {Promise} Pharmacy details
   */
  getPharmacyById: async (id) => {
    try {
      const response = await apiService.get(`/pharmacies/${id}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch pharmacy');
    }
  },

  /**
   * Get nearby pharmacies
   * @param {Object} params - Query parameters (latitude, longitude, radius)
   * @returns {Promise} List of nearby pharmacies
   */
  getNearbyPharmacies: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await apiService.get(`/pharmacies/nearby${queryString ? `?${queryString}` : ''}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch nearby pharmacies');
    }
  },

  // ==================== MEDICINES ====================

  /**
   * Get all medicines
   * @param {Object} params - Query parameters
   * @returns {Promise} List of medicines
   */
  getMedicines: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await apiService.get(`/medicines${queryString ? `?${queryString}` : ''}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch medicines');
    }
  },

  /**
   * Get medicine by ID
   * @param {string} id - Medicine ID
   * @returns {Promise} Medicine details
   */
  getMedicineById: async (id) => {
    try {
      const response = await apiService.get(`/medicines/${id}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch medicine');
    }
  },

  // ==================== AMBULANCE SERVICES ====================

  /**
   * Request ambulance service
   * @param {Object} requestData - Emergency request details
   * @returns {Promise} Request response
   */
  requestAmbulance: async (requestData) => {
    return await apiService.ambulances.request(requestData);
  },

  /**
   * Get ambulance requests
   * @param {Object} params - Query parameters
   * @returns {Promise} List of ambulance requests
   */
  getAmbulanceRequests: async (params = {}) => {
    return await apiService.ambulances.getRequests(params);
  },

  /**
   * Get ambulance request by ID
   * @param {string} id - Request ID
   * @returns {Promise} Request details
   */
  getAmbulanceRequestById: async (id) => {
    return await apiService.ambulances.getRequestById(id);
  },

  /**
   * Cancel ambulance request
   * @param {string} id - Request ID
   * @param {string} reason - Cancellation reason
   * @returns {Promise} Cancellation response
   */
  cancelAmbulanceRequest: async (id, reason) => {
    return await apiService.ambulances.cancelRequest(id, reason);
  },

  // ==================== HOSPITALS ====================

  /**
   * Get all hospitals
   * @param {Object} params - Query parameters
   * @returns {Promise} List of hospitals
   */
  getHospitals: async (params = {}) => {
    return await apiService.hospitals.getAll(params);
  },

  /**
   * Get hospital by ID
   * @param {string} id - Hospital ID
   * @returns {Promise} Hospital details
   */
  getHospitalById: async (id) => {
    return await apiService.hospitals.getById(id);
  },

  /**
   * Get nearby hospitals
   * @param {Object} params - Query parameters (latitude, longitude, radius)
   * @returns {Promise} List of nearby hospitals
   */
  getNearbyHospitals: async (params = {}) => {
    return await apiService.hospitals.getNearby(params);
  },

  /**
   * Search hospitals
   * @param {string} query - Search query
   * @param {Object} filters - Additional filters
   * @returns {Promise} Search results
   */
  searchHospitals: async (query, filters = {}) => {
    return await apiService.hospitals.search(query, filters);
  },

  /**
   * Get hospital services
   * @param {string} id - Hospital ID
   * @returns {Promise} Hospital services
   */
  getHospitalServices: async (id) => {
    return await apiService.hospitals.getServices(id);
  },

  /**
   * Get hospital doctors
   * @param {string} id - Hospital ID
   * @param {Object} params - Query parameters
   * @returns {Promise} List of doctors
   */
  getHospitalDoctors: async (id, params = {}) => {
    return await apiService.hospitals.getDoctors(id, params);
  },

  /**
   * Get hospital reviews
   * @param {string} id - Hospital ID
   * @param {Object} params - Query parameters
   * @returns {Promise} List of reviews
   */
  getHospitalReviews: async (id, params = {}) => {
    return await apiService.hospitals.getReviews(id, params);
  },

  // ==================== DOCTORS ====================

  /**
   * Get all doctors
   * @param {Object} params - Query parameters
   * @returns {Promise} List of doctors
   */
  getDoctors: async (params = {}) => {
    return await apiService.doctors.getAll(params);
  },

  /**
   * Get doctor by ID
   * @param {string} id - Doctor ID
   * @returns {Promise} Doctor details
   */
  getDoctorById: async (id) => {
    return await apiService.doctors.getById(id);
  },

  /**
   * Search doctors
   * @param {string} query - Search query
   * @param {Object} filters - Additional filters
   * @returns {Promise} Search results
   */
  searchDoctors: async (query, filters = {}) => {
    return await apiService.doctors.search(query, filters);
  },

  /**
   * Get doctor availability
   * @param {string} doctorId - Doctor ID
   * @param {string} date - Date to check availability
   * @returns {Promise} Availability slots
   */
  getDoctorAvailability: async (doctorId, date) => {
    return await apiService.doctors.getAvailability(doctorId, date);
  },

  // ==================== DASHBOARD ====================

  /**
   * Get patient dashboard data
   * @returns {Promise} Dashboard statistics and recent activity
   */
  getDashboardData: async () => {
    return await apiService.patients.getDashboard();
  },

  /**
   * Get patient statistics
   * @param {Object} params - Query parameters
   * @returns {Promise} Patient statistics
   */
  getPatientStats: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await apiService.get(`/patients/stats${queryString ? `?${queryString}` : ''}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch statistics');
    }
  },

  // ==================== NOTIFICATIONS ====================

  /**
   * Get patient notifications
   * @param {Object} params - Query parameters
   * @returns {Promise} List of notifications
   */
  getNotifications: async (params = {}) => {
    return await apiService.patients.getNotifications(params);
  },

  /**
   * Mark notification as read
   * @param {string} id - Notification ID
   * @returns {Promise} Update response
   */
  markNotificationRead: async (id) => {
    return await apiService.patients.markNotificationRead(id);
  },

  /**
   * Mark all notifications as read
   * @returns {Promise} Update response
   */
  markAllNotificationsRead: async () => {
    return await apiService.patients.markAllNotificationsRead();
  },

  // ==================== DEPENDENTS MANAGEMENT ====================

  /**
   * Get all dependents for the authenticated patient
   * @returns {Promise} List of dependents
   */
  getDependents: async () => {
    try {
      const response = await apiService.get('/patients/dependents');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch dependents');
    }
  },

  /**
   * Get a specific dependent by ID
   * @param {string} id - Dependent ID
   * @returns {Promise} Dependent details
   */
  getDependentById: async (id) => {
    try {
      const response = await apiService.get(`/patients/dependents/${id}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch dependent');
    }
  },

  /**
   * Add a new dependent (child under 18)
   * @param {Object} dependentData - Dependent information
   * @returns {Promise} Created dependent
   */
  addDependent: async (dependentData) => {
    try {
      const response = await apiService.post('/patients/dependents', dependentData);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to add dependent');
    }
  },

  /**
   * Update a dependent's information
   * @param {string} id - Dependent ID
   * @param {Object} dependentData - Updated dependent information
   * @returns {Promise} Updated dependent
   */
  updateDependent: async (id, dependentData) => {
    try {
      const response = await apiService.put(`/patients/dependents/${id}`, dependentData);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update dependent');
    }
  },

  /**
   * Remove a dependent
   * @param {string} id - Dependent ID
   * @returns {Promise} Deletion response
   */
  removeDependent: async (id) => {
    try {
      const response = await apiService.delete(`/patients/dependents/${id}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to remove dependent');
    }
  },

  /**
   * Add allergy to dependent
   * @param {string} dependentId - Dependent ID
   * @param {Object} allergyData - Allergy information
   * @returns {Promise} Updated allergies list
   */
  addDependentAllergy: async (dependentId, allergyData) => {
    try {
      const response = await apiService.post(`/patients/dependents/${dependentId}/allergies`, allergyData);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to add allergy');
    }
  },

  /**
   * Add medication to dependent
   * @param {string} dependentId - Dependent ID
   * @param {Object} medicationData - Medication information
   * @returns {Promise} Updated medications list
   */
  addDependentMedication: async (dependentId, medicationData) => {
    try {
      const response = await apiService.post(`/patients/dependents/${dependentId}/medications`, medicationData);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to add medication');
    }
  },

  /**
   * Get complete medical history for a dependent
   * @param {string} dependentId - Dependent ID
   * @returns {Promise} Medical history
   */
  getDependentMedicalHistory: async (dependentId) => {
    try {
      const response = await apiService.get(`/patients/dependents/${dependentId}/medical-history`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch dependent medical history');
    }
  },

  /**
   * Get consultations for a specific dependent
   * @param {string} dependentId - Dependent ID
   * @returns {Promise} Consultation history
   */
  getDependentConsultations: async (dependentId) => {
    try {
      const response = await apiService.get(`/consultations/dependents/${dependentId}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch dependent consultations');
    }
  },

  /**
   * Book consultation for a dependent
   * @param {Object} consultationData - Consultation details including dependentId
   * @returns {Promise} Created consultation
   */
  bookDependentConsultation: async (consultationData) => {
    try {
      // Ensure patientType is set to 'dependent'
      const data = {
        ...consultationData,
        patientType: 'dependent'
      };
      const response = await apiService.post('/consultations/book', data);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to book consultation for dependent');
    }
  }
};

export default patientService;